import React, { useState, useContext } from "react";
import { AppContext } from "../context";
import Navbar from "./Navbar";
import OtpVerification from "./OtpVerification";

/**
 * A passed verification is remembered in localStorage, so re-opening the link or
 * a sudden refresh does not demand a fresh SMS. It carries its own expiry and is
 * scoped to the link, so it cannot carry over to another claim.
 *
 * localStorage rather than sessionStorage because sessionStorage dies with the
 * tab: closing the link and opening it again would have asked for a new code.
 * The backend keeps a matching flag for the same hour (see utils/otp.js).
 */
const OTP_TOKEN_TTL_MS = 60 * 60 * 1000; // 1 hour

/**
 * Identity of the link, read from the URL fragment -- the app takes its own token
 * from window.location.hash (see Loading.js), and a refresh keeps the URL, so
 * this is the same string on every load of the same link.
 *
 * Deliberately NOT the context's inspectionId: setInspectionId is called on one
 * branch only, so on most flows it is still null when Liberty renders. Writing
 * under an id and reading back without one (or the reverse) misses the token and
 * asks the user to verify all over again.
 */
const linkKey = () => {
  try {
    const fromHash = (window.location.hash || "").split("#")[1];
    return fromHash ? fromHash.slice(0, 80) : "na";
  } catch (e) {
    return "na";
  }
};

const otpStorageKey = () => `otp_verified_${linkKey()}`;

/** True when a stored pass exists and is still in date. Clears a stale one. */
const readOtpToken = () => {
  try {
    const raw = localStorage.getItem(otpStorageKey());
    if (!raw) return false;
    const { exp } = JSON.parse(raw);
    if (typeof exp === "number" && Date.now() < exp) return true;
    localStorage.removeItem(otpStorageKey());
    return false;
  } catch (e) {
    // Unparseable, or storage blocked in private mode: just verify again.
    return false;
  }
};

const writeOtpToken = () => {
  try {
    localStorage.setItem(
      otpStorageKey(),
      JSON.stringify({ exp: Date.now() + OTP_TOKEN_TTL_MS })
    );
  } catch (e) {
    /* non-fatal: the pass simply will not survive a reload */
  }
};

/**
 * Claim progress stages, in order. Codes come from the client's stage payload;
 * the names are what the tracker shows.
 */
const STAGES = [
  { code: "A", name: "Claim Registered" },
  { code: "B", name: "Surveyor Appointed" },
  { code: "C", name: "Survey Completed" },
  { code: "D", name: "Work Approval" },
  { code: "E", name: "Re-inspection" },
  { code: "F", name: "Pre-Invoice / Tax Invoice" },
  { code: "G", name: "Delivery Order" },
  { code: "H", name: "Claim Payment Status" },
];

/** Keys the stage might arrive under. inputMetaData is a free-form blob the
 *  client posts when the token is created, so the name is not fixed yet. */
const STAGE_KEYS = [
  "claim_stage",
  "claim_status",
  "claimStage",
  "claimStatus",
  "stage",
  "status",
];

/** Lowercase, and reduce punctuation to single spaces, for tolerant matching. */
const normalise = (value) =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/**
 * Which stage the claim is at (1-based), or null when the payload does not say.
 *
 * Accepts a number (3), a stage code ("C"), the exact stage name, or something
 * close to it ("survey complete", "Stage C - Survey Completed"), under any of the
 * keys above. Partial matching needs four characters, so a stray "re" cannot
 * land on "Claim Registered".
 *
 * Returns null when the payload says nothing; the caller decides what to show
 * then (see DEFAULT_STAGE).
 */
const resolveStage = (meta) => {
  let raw = null;
  for (const key of STAGE_KEYS) {
    const value = meta && meta[key];
    if (value !== undefined && value !== null && value !== "") {
      raw = value;
      break;
    }
  }
  if (raw === null) return null;

  const asNumber = Number(raw);
  if (Number.isInteger(asNumber) && asNumber >= 1 && asNumber <= STAGES.length)
    return asNumber;

  const text = normalise(raw);
  if (!text) return null;

  const byCode = STAGES.findIndex((s) => s.code.toLowerCase() === text);
  if (byCode !== -1) return byCode + 1;

  const byName = STAGES.findIndex((s) => normalise(s.name) === text);
  if (byName !== -1) return byName + 1;

  if (text.length < 4) return null;
  const byPartial = STAGES.findIndex((s) => {
    const name = normalise(s.name);
    return name.includes(text) || text.includes(name);
  });
  return byPartial === -1 ? null : byPartial + 1;
};

/**
 * Where the tracker starts before the payload says otherwise.
 *
 * A link only exists once the claim is registered and a surveyor has been
 * appointed to it -- that surveyor is who sends it -- so those two stages are
 * behind every claim that reaches this page, whether or not the payload carries
 * a status. Stage 2 fills both segments; anything the payload does say replaces
 * it, in either direction.
 */
const DEFAULT_STAGE = 2;

/** A label / value pair in one of the detail grids. */
const Field = ({ label, value }) => (
  <div className="claim-field">
    <div className="claim-field-label">{label}</div>
    <div className="claim-field-value">{value || "--"}</div>
  </div>
);

const Liberty = () => {
  const {
    skipStencil,
    setSkipStencil,
    alignStencil,
    setalignStencil,
    capturedStencil,
    setcapturedStencil,
    stepCovered,
    setStepCovered,
    setScreen,
    currentBlock,
    condition,
    setCondition,
    config,
    token,
    mandatory,
    setMandatory,
    inspectionId,
    setStopPing,
    setCurrentPage,
    stopPing,
    restrictUserIfallNotGreen,
    setRestrictUserIfallNotGreen,
    clientId,
    caseId,
    isIOS,
    inputMetaData,
  } = useContext(AppContext);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Same primary colour as every other button in the app; the navy fallback is
  // the shade this page was designed in.
  const accent =
    (config && config["colors"] && config["colors"]["btn"]) || "#1c1550";

  // Whatever the payload says, or the two stages every claim has already been
  // through by the time this link is opened.
  const stage = resolveStage(inputMetaData) ?? DEFAULT_STAGE;

  // Verification is a step on the way OUT of this screen, not a gate in front of
  // it: the claim details are shown first, and the number is verified when the
  // user presses Proceed. A pass from the last hour is honoured, so a returning
  // user goes straight through -- the key comes from the URL, known on the very
  // first render, and expiry is only consulted here, on arrival.
  const [phoneVerified, setPhoneVerified] = useState(() => readOtpToken());
  const [showOtp, setShowOtp] = useState(false);

  const goToNextScreen = () => {
    if (config["email"]) {
      setScreen("email");
    } else {
      setScreen("instructions");
    }
  };

  const handleProceed = () => {
    if (!acceptedTerms) return;
    // Already verified within the hour: no second SMS, straight on.
    if (phoneVerified) {
      goToNextScreen();
      return;
    }
    setShowOtp(true);
  };

  const handleVerified = () => {
    writeOtpToken();
    setPhoneVerified(true);
    // The user pressed Proceed to get here, so carry on rather than dropping them
    // back on the claim page to press it again.
    goToNextScreen();
  };

  if (showOtp) {
    return <OtpVerification onVerified={handleVerified} />;
  }

  // One variable drives the button, the progress bar and the links, so a client's
  // primary colour styles the whole page.
  return (
    <div className="claim-page" style={{ "--claim-accent": accent }}>
      <Navbar />

      <div className="claim-card">
        <div className="claim-scroll">
          {/* The bar always shows a stage now: the payload's, or DEFAULT_STAGE
              when it carries none. */}
          <div className="claim-stage-tracker">
            <div className="claim-stage-head">
              <span className="claim-stage-label">Claim Progress</span>
              <span className="claim-stage-count">
                {`Stage ${stage} of ${STAGES.length}`}
              </span>
            </div>
            <div className="claim-stage-bar">
              {STAGES.map((s, i) => (
                <div
                  key={s.code}
                  className={`claim-stage-seg ${
                    i + 1 < stage
                      ? "claim-done"
                      : i + 1 === stage
                        ? "claim-current"
                        : ""
                  }`}
                />
              ))}
            </div>
            <p className="claim-stage-name">{STAGES[stage - 1].name}</p>
          </div>

          <div className="claim-section">
            <h2 className="claim-section-title">Claim Details</h2>
            <div className="claim-field-grid">
              <Field label="Claim Number:" value={inputMetaData?.claim_number} />
              <Field
                label="Intimation Date:"
                value={inputMetaData?.intimation_date}
              />
              <Field
                label="Policy Number:"
                value={inputMetaData?.policy_number}
              />
            </div>
          </div>

          <div className="claim-section">
            <h2 className="claim-section-title">Vehicle</h2>
            <div className="claim-field-grid">
              <Field
                label="Manufacturer:"
                value={inputMetaData?.manufacturer_name}
              />
              <Field label="Model:" value={inputMetaData?.model} />
              <Field label="Variant:" value={inputMetaData?.variant} />
              <Field
                label="Registration No:"
                value={inputMetaData?.vehicle_number}
              />
              <Field
                label="Year of Registration:"
                value={inputMetaData?.year_of_manufacture}
              />
            </div>
          </div>

          <div className="claim-section claim-section-last">
            <h2 className="claim-section-title">Insured</h2>
            <div className="claim-field-value">
              {inputMetaData?.insured_name || "--"}
            </div>
          </div>

        </div>

        {/* Pinned to the bottom of the card, outside the scroll area, so the
            consent and the action stay reachable however long the details are. */}
        <div className="claim-footer">
          <div className="claim-consent">
            <input
              type="checkbox"
              id="termsCheckbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
            />
            <label htmlFor="termsCheckbox">
              I have read and agree the Terms of Service and Privacy Policy.
            </label>
          </div>

          <div className="claim-cta-wrap">
            <button
              className={`claim-primary-btn ${
                acceptedTerms ? "claim-enabled" : ""
              }`}
              onClick={handleProceed}
              disabled={!acceptedTerms}
            >
              Proceed
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Liberty;
